import { buildingById } from "@zend/content";
import { AssetIcon } from "../../assets/Icon.js";
import { useT } from "../../i18n/index.js";
import { Button } from "../../ui/Button.js";
import { Icon } from "../../ui/Icon.js";
import { formatDuration } from "../../ui/Timer.js";
import { CostRow, buildingDescription, buildingName } from "./shared.js";
import { PayAction } from "./PayAction.js";
import type { CatalogueEntry } from "./buildCatalogue.js";
import "../building-panels.css";

/** One catalogue card: image first, one primary action, costs only where a decision is made (R10, D65). */
export function BuildingCard({ entry, nextHq, onBuild, onOpen }: { entry: CatalogueEntry; nextHq: number; onBuild: (cover: boolean) => void; onOpen: (rowId: string) => void }) {
  const t = useT();
  const def = buildingById(entry.buildingId);
  if (def.footprint === "ring") return null;
  const { info, group } = entry;
  const level = entry.builtRows.reduce((m, r) => Math.max(m, r.level), 0) || 1;
  return (
    <div
      className={`card building-card group-${group} ${entry.needed ? "needed" : ""}`}
      data-testid={`build-${def.id}`}
      data-group={group}
      data-new={entry.isNew || undefined}
      data-affordable={group === "ready" ? String(entry.affordable) : undefined}
      aria-disabled={group === "locked" || undefined}
    >
      {entry.isNew ? <span className="ribbon" data-testid="build-new">{t("base.build.new")}</span> : null}
      <div className="building-card-art">
        <AssetIcon id={def.id} buildingLevel={level} size={144} />
        <span className="badge dim">{`${def.footprint.w}×${def.footprint.h}`}</span>
      </div>
      <div className="building-card-copy">
        <strong>{buildingName(def.id)}</strong>
        <p className="hint ellipsis-2">{buildingDescription(def.id)}</p>
        {entry.needed ? <span className="badge warn" data-testid="build-needed">{t("base.build.needed", { n: nextHq })}</span> : null}
        {group === "ready" ? (
          <div className="row building-card-cost" style={{ gap: 8, flexWrap: "wrap" }}>
            <CostRow cost={info.placement.cost} compact />
            <span className="mono hint"><Icon name="clock" /> {formatDuration(info.placement.timeMs)}</span>
          </div>
        ) : null}
        {group === "built" ? (
          <span className="hint">
            {entry.builtRows.map((r) => `Lv ${r.level}`).join(" · ")}
            {info.nextCopyHq ? ` · ${t("base.build.nextCopy", { n: info.nextCopyHq })}` : ""}
          </span>
        ) : null}
      </div>
      {group === "locked" ? (
        <span className="building-card-status"><Icon name="lock" /> {t("base.build.locked", { n: info.unlockHq })}</span>
      ) : group === "ready" ? (
        entry.fits ? (
          <PayAction cost={info.placement.cost} reasons={[]} onStart={(cover) => onBuild(cover)} label={<><Icon name="plus" /> {t("base.build.action")}</>} testId={`build-action-${def.id}`} showReasons={false} style={{ marginTop: 0 }} />
        ) : (
          <Button variant="primary" className="building-card-action" disabled data-testid={`build-action-${def.id}`}>{t("base.build.noRoom")}</Button>
        )
      ) : (
        <Button className="building-card-action" onClick={() => onOpen(entry.builtRows[0]!.rowId)} data-testid={`build-action-${def.id}`}>
          <Icon name="arrowUp" /> {t("base.build.upgrade")}
        </Button>
      )}
    </div>
  );
}
